/**
 * 사업자 크레딧 관리
 *
 *  운영팀이 사장님 문의 대응 시 즉시 보너스 지급·차감하고, 트랜잭션 이력을 조회.
 *  좌측: 사업장 검색·정렬·잔액 테이블
 *  우측: 선택된 사업장의 트랜잭션 로그 + 관리자 액션 (지급/차감)
 */
import { useEffect, useState } from 'react';
import { Search, Plus, Minus, Building2, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  creditApi,
  CompanyWallet,
  CompanyCreditUsage,
  CompanyTxItem,
  CdCreditPlan,
  CdFundSource,
  CreditGrantCategory,
  ExpireMode,
  GrantRequest,
  CreditPackage,
  CREDIT_TX_LABEL,
  FUND_SOURCE_META,
  GRANT_CATEGORY_META,
  VISIBLE_GRANT_CATEGORIES,
} from '@/services/api/credit';

const formatNum = (n: number | null | undefined) => Number(n ?? 0).toLocaleString();
/** Date → 'yyyy-MM-dd' (로컬 기준, 기간검색 파라미터용) */
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

type SortKey = 'balance' | 'monthlyUsed' | 'lifetimeUsed';

const PAGE_SIZE = 20;

/* ─────────────────────────────────────────────────────────────────
 *  단일 사업장 크레딧 패널 — 매장 목록 '크레딧' 탭에 임베드해서 사용.
 *  잔액 요약 + 트랜잭션 로그 + 관리자 액션(지급/차감/요금제/면제/환원) 일체.
 * ───────────────────────────────────────────────────────────────── */
export function CompanyCreditPanel({ companySeq }: { companySeq: number }) {
  const [modal, setModal] = useState<
    | null
    | { type: 'grant' | 'deduct'; companySeq: number; companyName: string; balance: number }
    | { type: 'plan'; companySeq: number; companyName: string; currentPlan: CdCreditPlan }
    | { type: 'exempt'; companySeq: number; companyName: string; exemptUntil: string | null }
    | { type: 'reverse'; tx: CompanyTxItem }
    | { type: 'bulkReverse'; companySeq: number; companyName: string; totalCount: number; totalCredits: number; refundableKrw: number }
  >(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);
  const done = () => { setModal(null); reload(); };

  return (
    <div className="h-[calc(100vh-320px)] min-h-[440px]">
      <CompanyDetailPanel
        key={`${companySeq}-${reloadKey}`}
        companySeq={companySeq}
        embedded
        onClose={() => {}}
        onAction={setModal}
      />

      {modal?.type === 'grant' && (
        <AdjustModal mode="grant" companySeq={modal.companySeq} companyName={modal.companyName} currentBalance={modal.balance}
          onClose={() => setModal(null)} onDone={done} />
      )}
      {modal?.type === 'deduct' && (
        <AdjustModal mode="deduct" companySeq={modal.companySeq} companyName={modal.companyName} currentBalance={modal.balance}
          onClose={() => setModal(null)} onDone={done} />
      )}
      {modal?.type === 'plan' && (
        <PlanChangeModal companySeq={modal.companySeq} companyName={modal.companyName} currentPlan={modal.currentPlan}
          onClose={() => setModal(null)} onDone={done} />
      )}
      {modal?.type === 'exempt' && (
        <ExemptionModal companySeq={modal.companySeq} companyName={modal.companyName} currentUntil={modal.exemptUntil}
          onClose={() => setModal(null)} onDone={done} />
      )}
      {modal?.type === 'reverse' && (
        <ReverseModal tx={modal.tx} onClose={() => setModal(null)} onDone={done} />
      )}
      {modal?.type === 'bulkReverse' && (
        <BulkReverseModal companySeq={modal.companySeq} companyName={modal.companyName}
          totalCount={modal.totalCount} totalCredits={modal.totalCredits} refundableKrw={modal.refundableKrw}
          onClose={() => setModal(null)} onDone={done} />
      )}
    </div>
  );
}

export function CreditCompaniesPage() {
  const [wallets, setWallets] = useState<CompanyWallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('balance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [page, setPage] = useState(1);   // 1-based
  const [modal, setModal] = useState<
    | null
    | { type: 'grant' | 'deduct'; companySeq: number; companyName: string; balance: number }
    | { type: 'plan'; companySeq: number; companyName: string; currentPlan: CdCreditPlan }
    | { type: 'exempt'; companySeq: number; companyName: string; exemptUntil: string | null }
    | { type: 'reverse'; tx: CompanyTxItem }
    | { type: 'bulkReverse'; companySeq: number; companyName: string; totalCount: number; totalCredits: number; refundableKrw: number }
  >(null);

  const load = async () => {
    setLoading(true);
    const list = await creditApi.listCompanyWallets({ q, sort, sortDir });
    setWallets(list);
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sort, sortDir]);

  // 검색/정렬 바뀌면 1페이지로 리셋
  useEffect(() => {
    setPage(1);
  }, [q, sort, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sort === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setSortDir('desc');
    }
  };

  // 페이징 계산
  const totalPages = Math.max(1, Math.ceil(wallets.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const pagedWallets = wallets.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    // 페이지 전체를 한 화면에 — 헤더(64) + main pt(0~) + p-5 + space 고려해 calc
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <header className="shrink-0 mb-3">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
          <Building2 className="text-gray-500" size={22} /> 사업자 크레딧
        </h1>
      </header>

      {/* 좌측 리스트 + 우측 상세 — Master-Detail (flex-1, 내부 스크롤) */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* ── 좌측: 사업자 리스트 (5/12) ── */}
        <div className="col-span-12 lg:col-span-5 flex flex-col min-h-0">
          {/* 검색 */}
          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="사업자명 / 원장명 / SEQ"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {/* 정렬 토글 */}
          <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              총 {wallets.length}개
              {wallets.length > PAGE_SIZE && (
                <span className="ml-1 text-gray-400">· {safePage}/{totalPages} 페이지</span>
              )}
            </span>
            <div className="flex gap-1">
              {([
                { k: 'balance' as SortKey,      l: '잔액' },
                { k: 'monthlyUsed' as SortKey,  l: '이번 달' },
                { k: 'lifetimeUsed' as SortKey, l: '누적' },
              ]).map(({ k, l }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleSort(k)}
                  className={`inline-flex items-center gap-0.5 rounded px-2 py-0.5 ${
                    sort === k ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {l}
                  {sort === k && (sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
                </button>
              ))}
            </div>
          </div>

          {/* 리스트 — flex-1 로 남은 공간 차지, 내부 스크롤 */}
          <div className="flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="h-full overflow-y-auto divide-y divide-gray-100">
              {loading && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">로딩 중…</div>
              )}
              {!loading && wallets.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-400">검색 결과가 없어요</div>
              )}
              {!loading && pagedWallets.map((w) => {
                const selected = selectedSeq === w.companySeq;
                const deleted = w.ownerActive === false;
                return (
                  <div
                    key={w.companySeq}
                    onClick={() => setSelectedSeq(w.companySeq)}
                    // 컨테이너의 divide-gray-100 규칙(명시도 0,3,0)이 border-color 단축 속성으로
                    // 좌측 테두리 색까지 덮어써서, ! 프리픽스로 선택 표시를 강제한다.
                    className={`cursor-pointer px-3 py-2.5 transition-colors ${
                      selected ? 'bg-primary-50 border-l-4 !border-l-primary-500 pl-2'
                               : 'border-l-4 !border-l-transparent hover:bg-gray-50'
                    } ${deleted ? 'bg-gray-50/50 opacity-70' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-medium text-sm truncate ${deleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                            {w.companyName ?? `사업장 #${w.companySeq}`}
                          </span>
                          {deleted && (
                            <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                              삭제된 사업자
                            </span>
                          )}
                          <PlanBadge plan={w.cdPlan} />
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-500 truncate">
                          {w.bizUserName ? `${w.bizUserName} · ` : ''}{w.region ?? ''} · SEQ {w.companySeq}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className={`text-base font-bold tabular-nums ${
                          deleted ? 'text-gray-400' :
                          (w.balance ?? 0) < 10 ? 'text-red-600' : (w.balance ?? 0) < 100 ? 'text-amber-600' : 'text-gray-900'
                        }`}>
                          {formatNum(w.balance)}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          <span className={deleted ? 'text-gray-400' : 'text-emerald-600'}>{formatNum(w.freeBalance)}</span>
                          <span className="mx-0.5">/</span>
                          <span>{formatNum(w.paidBalance)}</span>
                        </div>
                      </div>
                    </div>
                    {/* 빠른 액션 (삭제된 사업자는 비활성) */}
                    <div className="mt-1.5 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                      <div className="text-[10px] text-gray-500">
                        이번달 -{formatNum(w.monthlyUsed)} · 누적 -{formatNum(w.lifetimeUsed)}
                      </div>
                      {!deleted && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => setModal({ type: 'grant', companySeq: w.companySeq, companyName: w.companyName ?? `사업장 #${w.companySeq}`, balance: w.balance })}
                            className="rounded-md px-1.5 py-0.5 text-[11px] text-emerald-600 hover:bg-emerald-50"
                          >
                            <Plus size={11} className="inline" /> 지급
                          </button>
                          <button
                            onClick={() => setModal({ type: 'deduct', companySeq: w.companySeq, companyName: w.companyName ?? `사업장 #${w.companySeq}`, balance: w.balance })}
                            className="rounded-md px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50"
                          >
                            <Minus size={11} className="inline" /> 차감
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 페이징 컨트롤 — shrink-0 으로 리스트 아래 고정 */}
          {!loading && wallets.length > PAGE_SIZE && (
            <div className="shrink-0 pt-2">
              <Pagination
                page={safePage}
                totalPages={totalPages}
                onChange={(p) => setPage(p)}
              />
            </div>
          )}
        </div>

        {/* ── 우측: 상세 (7/12) — h-full + 내부 스크롤 ── */}
        <div className="col-span-12 lg:col-span-7 min-h-0">
          {selectedSeq === null ? (
            <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
              <div className="text-center">
                <Building2 className="mx-auto mb-2 text-gray-300" size={40} />
                <div className="text-sm text-gray-500">좌측에서 사업자를 선택하면<br/>상세 정보가 표시됩니다</div>
              </div>
            </div>
          ) : (
            <CompanyDetailPanel
              key={selectedSeq + String(modal === null)}
              companySeq={selectedSeq}
              onClose={() => setSelectedSeq(null)}
              onAction={setModal}
            />
          )}
        </div>
      </div>

      {/* 모달 분기 */}
      {modal?.type === 'grant' && (
        <AdjustModal
          mode="grant" companySeq={modal.companySeq} companyName={modal.companyName} currentBalance={modal.balance}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'deduct' && (
        <AdjustModal
          mode="deduct" companySeq={modal.companySeq} companyName={modal.companyName} currentBalance={modal.balance}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'plan' && (
        <PlanChangeModal
          companySeq={modal.companySeq} companyName={modal.companyName} currentPlan={modal.currentPlan}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'exempt' && (
        <ExemptionModal
          companySeq={modal.companySeq} companyName={modal.companyName} currentUntil={modal.exemptUntil}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'reverse' && (
        <ReverseModal
          tx={modal.tx}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'bulkReverse' && (
        <BulkReverseModal
          companySeq={modal.companySeq}
          companyName={modal.companyName}
          totalCount={modal.totalCount}
          totalCredits={modal.totalCredits}
          refundableKrw={modal.refundableKrw}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
 *  플랜 배지
 * ───────────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────────
 *  페이징 컨트롤 — Prev / 페이지 번호 / Next
 *  총 페이지가 7 이하면 모두 표시, 초과 시 양 끝 + 현재 주변 표시 ("…" 생략)
 * ───────────────────────────────────────────────────────────────── */
function Pagination({
  page, totalPages, onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  const pages: (number | '...')[] = (() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const out: (number | '...')[] = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) out.push('...');
    for (let i = start; i <= end; i++) out.push(i);
    if (end < totalPages - 1) out.push('...');
    out.push(totalPages);
    return out;
  })();

  const btnBase = 'inline-flex h-7 min-w-[28px] items-center justify-center rounded text-xs transition-colors';

  return (
    <div className="mt-3 flex items-center justify-center gap-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className={`${btnBase} px-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent`}
      >
        <ChevronLeft size={14} />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`dot-${i}`} className="px-1 text-xs text-gray-400">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`${btnBase} px-2 ${
              p === page
                ? 'bg-primary-500 text-white font-semibold'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className={`${btnBase} px-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent`}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

const PLAN_STYLE: Record<string, { label: string; cls: string }> = {
  PLAN_FREE:     { label: 'Free',     cls: 'bg-gray-100 text-gray-600' },
  PLAN_BASIC:    { label: 'Basic',    cls: 'bg-blue-50 text-blue-700' },
  PLAN_PRO:      { label: 'Pro',      cls: 'bg-purple-50 text-purple-700' },
  PLAN_BUSINESS: { label: 'Business', cls: 'bg-emerald-50 text-emerald-700' },
};
function PlanBadge({ plan }: { plan?: string }) {
  const key = plan ?? 'PLAN_FREE';
  const meta = PLAN_STYLE[key] ?? PLAN_STYLE.PLAN_FREE;
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  선택된 사업장 상세 — 트랜잭션 로그 + 액션 버튼
 * ───────────────────────────────────────────────────────────────── */
type ModalDispatcher = (
  m:
    | { type: 'grant'; companySeq: number; companyName: string; balance: number }
    | { type: 'deduct'; companySeq: number; companyName: string; balance: number }
    | { type: 'plan'; companySeq: number; companyName: string; currentPlan: CdCreditPlan }
    | { type: 'exempt'; companySeq: number; companyName: string; exemptUntil: string | null }
    | { type: 'reverse'; tx: CompanyTxItem }
    | { type: 'bulkReverse'; companySeq: number; companyName: string; totalCount: number; totalCredits: number; refundableKrw: number }
) => void;

function CompanyDetailPanel({
  companySeq,
  onClose,
  onAction,
  embedded = false,
}: {
  companySeq: number;
  onClose: () => void;
  onAction: ModalDispatcher;
  embedded?: boolean;
}) {
  const [data, setData] = useState<{ wallet: CompanyWallet; transactions: CompanyTxItem[] } | null>(null);
  // 상단 요약 접기/펴기 — 트랜잭션 공간 확보
  const [summaryOpen, setSummaryOpen] = useState(true);
  // 트랜잭션 기간검색 — 디폴트 최근 1개월
  const [range, setRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    return { from: ymd(from), to: ymd(to) };
  });
  // 선택된 빠른선택(개월). 날짜를 직접 바꾸면 null(직접입력)
  const [preset, setPreset] = useState<number | null>(1);
  const applyPreset = (months: number) => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - months);
    setRange({ from: ymd(from), to: ymd(to) });
    setPreset(months);
  };

  // 매장이 바뀌면 초기화(로딩 표시), 기간만 바뀌면 기존 화면 유지한 채 재조회
  useEffect(() => {
    setData(null);
  }, [companySeq]);
  useEffect(() => {
    creditApi.getCompanyWallet(companySeq, range.from, range.to).then(setData);
  }, [companySeq, range.from, range.to]);

  if (!data) {
    return (
      <div className="h-full rounded-lg border border-gray-200 bg-white p-6">
        <div className="h-20 animate-pulse rounded bg-gray-100" />
      </div>
    );
  }

  const { wallet, transactions } = data;

  return (
    <div className={`flex h-full flex-col rounded-lg bg-white overflow-hidden ${embedded ? 'border border-gray-200' : 'border-2 border-primary-200'}`}>
      <div className="shrink-0 flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900 truncate">
            {wallet.companyName} <span className="text-xs font-normal text-gray-500">SEQ {wallet.companySeq}</span>
          </h2>
          <p className="text-xs text-gray-500 truncate">
            {wallet.bizUserName ? `원장 ${wallet.bizUserName} · ` : ''}{wallet.region}
          </p>
        </div>
        {/* 관리자 액션 — 지급 / 차감 / 요금제 / 면제 */}
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={() => onAction({ type: 'grant', companySeq: wallet.companySeq, companyName: wallet.companyName ?? `사업장 #${wallet.companySeq}`, balance: wallet.balance })}
            className="rounded-md border border-emerald-200 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
          >
            <Plus size={11} className="inline" /> 지급
          </button>
          <button
            onClick={() => onAction({ type: 'deduct', companySeq: wallet.companySeq, companyName: wallet.companyName ?? `사업장 #${wallet.companySeq}`, balance: wallet.balance })}
            className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
          >
            <Minus size={11} className="inline" /> 차감
          </button>
          <button
            onClick={() => onAction({ type: 'exempt', companySeq: wallet.companySeq, companyName: wallet.companyName ?? `사업장 #${wallet.companySeq}`, exemptUntil: wallet.exemptUntil ?? null })}
            className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            면제
          </button>
          {!embedded && (
            <button onClick={onClose} className="ml-1 text-sm text-gray-500 hover:text-gray-900">닫기 ×</button>
          )}
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0">
        {/* 잔액 요약 — 접기/펴기(트랜잭션 공간 확보) */}
        <div className="shrink-0 border-b border-gray-200 px-4 py-2">
          <button
            type="button"
            onClick={() => setSummaryOpen((o) => !o)}
            className="flex w-full items-center justify-between py-1 text-[11px] font-semibold text-gray-500 hover:text-gray-700"
          >
            <span>잔액 · 소모 분석{!summaryOpen && ` · 현재 ${formatNum(wallet.balance)} 크레딧`}</span>
            {summaryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {summaryOpen && (
          <div className="space-y-3 pt-2">
          {/* 히어로 — 잔액 크게 + 무료/충전 우측 */}
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-[11px] text-gray-500">현재 잔액</div>
              <div className="mt-0.5 text-2xl font-bold text-gray-900 leading-none">
                {formatNum(wallet.balance)}
                <span className="ml-1 text-xs font-normal text-gray-500">크레딧</span>
              </div>
            </div>
            <div className="flex gap-4 text-right">
              <div>
                <div className="text-[10px] text-gray-500">무료</div>
                <div className="text-sm font-bold text-emerald-600 leading-none">{formatNum(wallet.freeBalance)}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">충전</div>
                <div className="text-sm font-bold text-gray-900 leading-none">{formatNum(wallet.paidBalance)}</div>
              </div>
            </div>
          </div>

          {/* 보조 통계 4분할 한 줄 */}
          <div className="grid grid-cols-4 gap-2 rounded-md bg-gray-50 px-3 py-2">
            <CompactStat label="이번 달 사용" value={formatNum(wallet.monthlyUsed)} />
            <CompactStat label="이번 달 충전" value={formatNum(wallet.monthlyPurchased)} />
            <CompactStat label="누적 지급"   value={formatNum(wallet.lifetimeGranted)} />
            <CompactStat label="누적 사용"   value={formatNum(wallet.lifetimeUsed)} />
          </div>

          {/* 소모율 / 소진예상 / 충전주기 */}
          <CreditUsageCard companySeq={companySeq} />

          {/* 환원 가능 총량 — 현재 트랜잭션 기준 합산 */}
          {(() => {
            const totals = computeReversibleTotals(transactions);
            if (totals.totalCount === 0) return null;
            return (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-blue-800">↶ 환원 가능 (현재 기준)</div>
                    <div className="mt-0.5 text-[10px] text-blue-600">
                      {totals.totalCount}건 · PG 환불 {totals.refundableKrw.toLocaleString()}원
                      {totals.refundFeeKrw > 0 && (
                        <span className="ml-1 text-blue-400">
                          (위약금 {totals.refundFeeKrw.toLocaleString()}원 차감 후)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-base font-bold text-blue-700 tabular-nums">
                      {formatNum(totals.totalCredits)}
                    </div>
                    <div className="text-[10px] text-blue-600">크레딧</div>
                  </div>
                  {/* 환원할 게 실제로 있을 때만 버튼 노출 (크레딧>0 또는 PG 환불액>0) */}
                  {(totals.totalCredits > 0 || totals.refundableKrw > 0) && (
                    <button
                      type="button"
                      onClick={() => onAction({
                        type: 'bulkReverse',
                        companySeq: wallet.companySeq,
                        companyName: wallet.companyName ?? `사업장 #${wallet.companySeq}`,
                        totalCount: totals.totalCount,
                        totalCredits: totals.totalCredits,
                        refundableKrw: totals.refundableKrw,
                      })}
                      className="shrink-0 rounded-md border border-blue-500 bg-white px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                      title="환원 가능한 모든 트랜잭션 일괄 환원"
                    >
                      ↶ 전체 환원
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {wallet.dtFreeExpire && (
            <div className="text-[11px] text-gray-500">
              무료분 만료: {new Date(wallet.dtFreeExpire).toLocaleDateString('ko-KR')}
            </div>
          )}

          {/* 면제 기간 안내 */}
          {wallet.exemptUntil && new Date(wallet.exemptUntil) > new Date() && (
            <div className="rounded-md bg-blue-50 px-3 py-1.5 text-[11px] text-blue-800">
              🛡 차감 면제 적용 중 — ~{new Date(wallet.exemptUntil).toLocaleDateString('ko-KR')}
              {wallet.exemptReason && <span className="ml-1 italic text-blue-600">· {wallet.exemptReason}</span>}
            </div>
          )}
          </div>
          )}
        </div>

        {/* 트랜잭션 로그 — flex-1 로 남은 공간 차지 */}
        <div className="flex flex-col flex-1 min-h-0 p-4">
          <div className="shrink-0 mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
              트랜잭션 <span className="ml-1 normal-case text-gray-400">({transactions.length}건)</span>
            </h3>
            {/* 기간검색 — 디폴트 최근 1개월 */}
            <div className="flex items-center gap-1 text-[11px] text-gray-600">
              <input
                type="date"
                value={range.from}
                max={range.to}
                onChange={(e) => { setRange((r) => ({ ...r, from: e.target.value })); setPreset(null); }}
                className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px]"
              />
              <span className="text-gray-400">~</span>
              <input
                type="date"
                value={range.to}
                min={range.from}
                onChange={(e) => { setRange((r) => ({ ...r, to: e.target.value })); setPreset(null); }}
                className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px]"
              />
              {/* 빠른 선택 — 선택된 항목 하이라이트 */}
              {([['1개월', 1], ['3개월', 3], ['6개월', 6]] as const).map(([label, months]) => {
                const active = preset === months;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => applyPreset(months)}
                    className={`rounded border px-1.5 py-0.5 text-[10px] ${
                      active
                        ? 'border-primary-500 bg-primary-50 font-semibold text-primary-700'
                        : 'border-gray-200 text-gray-500 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex-1 min-h-0 space-y-1.5 overflow-y-auto">
            {transactions.length === 0 && (
              <div className="py-6 text-center text-xs text-gray-400">해당 기간 트랜잭션 없음</div>
            )}
            {transactions.map((tx) => (
              <TxRow key={tx.txSeq} tx={tx} onReverse={(t) => onAction({ type: 'reverse', tx: t })} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  환원 가능량 계산 헬퍼
 *
 *  정책:
 *   - 무료 지급(ADMIN_GRANT / SYSTEM_GRANT / PROMOTION / REVERSAL_RESTORE) → 환원 X (회수 의미 X)
 *   - PG 결제 (PG_PAYMENT / SETTLEMENT_CONVERT, CREDIT) → 잔량(remainingAmt) 만큼 회수 가능
 *   - 사용 (USE_*, DEBIT) → 전액 복원 가능 (잘못된 차감 되돌림)
 *   - 이미 환원된 row / REVERSAL row / EXPIRED 는 0
 *
 *  환불 수수료 (PG 결제 한정 · 전자금융거래법 / 전자상거래법):
 *   - 충전 후 7일 이내 + 한 번도 사용 안 됨 (remainingAmt === amount) → 청약철회 100% 환불
 *   - 그 외 (7일 이내 + 사용 흔적 있음, 또는 7일 초과) → 잔량 환불액의 90% (10% 위약금)
 *   - 단가: 잔량 × (실결제액 / 충전 크레딧) [Lot 단가] × 환불비율
 *   - purchasePriceKrw 가 없으면 0.
 * ───────────────────────────────────────────────────────────────── */
const REFUND_FEE_RATE = 0.1;          // 10% 위약금
const REFUND_GRACE_DAYS = 7;          // 청약철회 기간

function computeReversibleAmount(tx: CompanyTxItem): {
  credits: number;
  refundableKrw: number;
  refundFeeKrw: number;
  feeApplied: boolean;
} {
  const zero = { credits: 0, refundableKrw: 0, refundFeeKrw: 0, feeApplied: false };
  if (tx.reversed) return zero;
  if (tx.cdTxType === 'REVERSAL') return zero;
  if (tx.expireStatus === 'EXPIRED') return zero;

  // ── 환원 대상은 CREDIT 방향(지급/충전) 만 ──
  //  DEBIT(USE_* 차감)은 이미 소비된 결과라 환원하지 않음.
  //  잘못된 차감 보정이 필요하면 admin 지급(grant)으로 별도 처리.
  if (tx.cdDirection !== 'CREDIT') return zero;

  const src = tx.cdFundSource;
  const isFree = src === 'ADMIN_GRANT' || src === 'SYSTEM_GRANT'
              || src === 'PROMOTION' || src === 'REVERSAL_RESTORE';
  if (isFree) return zero;
  const rem = tx.remainingAmt ?? 0;
  if (rem <= 0) return zero;

  // PG 결제 한정 환불 금액 = 잔량 × (실결제액 / 충전 크레딧 총량) [Lot 단가]
  let grossKrw = 0;
  let feeKrw = 0;
  let feeApplied = false;
  if ((src === 'PG_PAYMENT' || src === 'SETTLEMENT_CONVERT')
      && tx.purchasePriceKrw && tx.amount > 0) {
    grossKrw = Math.floor(tx.purchasePriceKrw * (rem / tx.amount));

    // 청약철회 무수수료 조건: 7일 이내 + 한 번도 사용 안 됨 (잔량 == 충전 총량)
    const daysSince = (Date.now() - new Date(tx.dtRegist).getTime()) / (1000 * 60 * 60 * 24);
    const untouched = rem === tx.amount;
    const withdrawalPeriod = daysSince <= REFUND_GRACE_DAYS && untouched;

    if (!withdrawalPeriod) {
      feeKrw = Math.floor(grossKrw * REFUND_FEE_RATE);
      feeApplied = true;
    }
  }
  return {
    credits: rem,
    refundableKrw: grossKrw - feeKrw,
    refundFeeKrw: feeKrw,
    feeApplied,
  };
}

/** 트랜잭션 전체 환원 가능 합산. */
function computeReversibleTotals(txs: CompanyTxItem[]): {
  totalCount: number;
  totalCredits: number;
  refundableKrw: number;
  refundFeeKrw: number;
} {
  let count = 0, credits = 0, krw = 0, fee = 0;
  for (const t of txs) {
    const r = computeReversibleAmount(t);
    if (r.credits > 0) {
      count++;
      credits += r.credits;
      krw += r.refundableKrw;
      fee += r.refundFeeKrw;
    }
  }
  return { totalCount: count, totalCredits: credits, refundableKrw: krw, refundFeeKrw: fee };
}

/** 컴팩트 4분할 통계 셀 — 라벨(작게) + 값(중간) 세로 스택. */
function CompactStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] text-gray-500 truncate">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-gray-900 tabular-nums truncate">{value}</div>
    </div>
  );
}

function CreditUsageCard({ companySeq }: { companySeq: number }) {
  const [usage, setUsage] = useState<CompanyCreditUsage | null>(null);
  const [win, setWin] = useState<30 | 90>(90);

  useEffect(() => {
    setUsage(null);
    creditApi.getCompanyCreditUsage(companySeq).then(setUsage).catch(() => setUsage(null));
  }, [companySeq]);

  if (!usage) {
    return (
      <div className="rounded-md border border-gray-200 px-3 py-2 text-[11px] text-gray-400">
        소모·충전 분석 불러오는 중…
      </div>
    );
  }

  const avg = win === 30 ? usage.avgDaily30 : usage.avgDaily90;
  const proj = win === 30 ? usage.projectedDays30 : usage.projectedDays90;
  const projLabel = avg <= 0 ? '최근 소모 없음' : proj != null ? `약 ${proj.toLocaleString()}일 후` : '-';
  const urgent = proj != null && proj <= 7;

  return (
    <div className="space-y-2 rounded-md border border-gray-200 px-3 py-2.5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold text-gray-700">소모·충전 분석</div>
        <div className="flex gap-1">
          {([30, 90] as const).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWin(w)}
              className={`rounded px-1.5 py-0.5 text-[10px] ${win === w ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'}`}
            >
              {w}일
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded bg-gray-50 px-2.5 py-1.5">
          <div className="text-[10px] text-gray-500">일평균 소모</div>
          <div className="text-sm font-bold text-gray-900">
            {avg.toLocaleString()}
            <span className="text-[10px] font-normal text-gray-500"> C/일</span>
          </div>
        </div>
        <div className={`rounded px-2.5 py-1.5 ${urgent ? 'bg-red-50' : 'bg-gray-50'}`}>
          <div className="text-[10px] text-gray-500">예상 소진</div>
          <div className={`text-sm font-bold ${urgent ? 'text-red-600' : 'text-gray-900'}`}>{projLabel}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded bg-gray-50 px-2.5 py-1.5">
          <div className="text-[10px] text-gray-500">평균 충전 간격(실측)</div>
          <div className="text-sm font-bold text-gray-900">
            {usage.avgRechargeIntervalDays != null ? `${usage.avgRechargeIntervalDays}일` : '-'}
          </div>
        </div>
        <div className="rounded bg-gray-50 px-2.5 py-1.5">
          <div className="text-[10px] text-gray-500">예상 충전주기</div>
          <div className="text-sm font-bold text-gray-900">
            {usage.estRechargeCycleDays ? `${usage.estRechargeCycleDays.toLocaleString()}일마다` : '-'}
          </div>
        </div>
      </div>
      <div className="text-[9px] leading-relaxed text-gray-400">
        예상 충전주기 = 최근 충전액(
        {usage.lastRechargeAmount != null ? usage.lastRechargeAmount.toLocaleString() : '-'}C) ÷ 90일 일평균.
        소모는 계약·발급 시 몰릴 수 있어 근사치입니다.
      </div>
    </div>
  );
}

function TxRow({ tx, onReverse }: { tx: CompanyTxItem; onReverse?: (tx: CompanyTxItem) => void }) {
  const isCredit = tx.cdDirection === 'CREDIT';
  const fundMeta = tx.cdFundSource ? FUND_SOURCE_META[tx.cdFundSource as CdFundSource] : undefined;

  // 만료 임박 계산 (CREDIT + 만료일 있음 + ACTIVE)
  const expireBadge = (() => {
    if (!isCredit || !tx.dtExpire || tx.expireStatus !== 'ACTIVE') return null;
    const days = Math.ceil((new Date(tx.dtExpire).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: '만료', cls: 'bg-red-100 text-red-700' };
    if (days <= 7) return { label: `${days}일 후 만료`, cls: 'bg-orange-100 text-orange-700' };
    if (days <= 30) return { label: `${days}일`, cls: 'bg-amber-50 text-amber-700' };
    return null;
  })();

  // 환원 가능 여부:
  //  - 무료 지급 (ADMIN_GRANT / SYSTEM_GRANT / PROMOTION) 은 환원 노출 안 함 (사장님 돈 아니라 회수 의미 없음)
  //  - PG 결제(PG_PAYMENT) / 정산 전환(SETTLEMENT_CONVERT) 충전은 환불 의무 → 환원 가능
  //  - 사용(DEBIT, USE_*) 은 잘못된 차감 복원 케이스 → 환원 가능
  //  - 이미 환원됐거나 REVERSAL 자체이거나 만료/소진된 건 불가
  const isFreeGrant = isCredit && (
    tx.cdFundSource === 'ADMIN_GRANT'
    || tx.cdFundSource === 'SYSTEM_GRANT'
    || tx.cdFundSource === 'PROMOTION'
    || tx.cdFundSource === 'REVERSAL_RESTORE'
  );
  const reversible = onReverse
    && isCredit                       // 환원 대상은 CREDIT(지급/충전) 만 — DEBIT(USE_* 차감) 제외
    && !tx.reversed
    && !isFreeGrant
    && tx.cdTxType !== 'REVERSAL'
    && tx.expireStatus !== 'EXPIRED'
    && (tx.remainingAmt ?? 0) > 0;

  return (
    <div className={`rounded border px-2.5 py-2 ${tx.reversed ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-900 truncate">
            {fundMeta && <span className="text-sm leading-none">{fundMeta.emoji}</span>}
            <span className="truncate">{tx.description}</span>
            {tx.reversed && <span className="rounded bg-gray-200 px-1 text-[10px] text-gray-600">환원됨</span>}
            {tx.cdTxType === 'REVERSAL' && (
              <span className="rounded bg-blue-50 px-1 text-[10px] text-blue-700">↶ 환원</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-gray-500">
            <span>{CREDIT_TX_LABEL[tx.cdTxType] ?? tx.cdTxType}</span>
            {fundMeta && (
              <>
                <span>·</span>
                <span className="rounded bg-gray-100 px-1 text-gray-600">{fundMeta.label}</span>
              </>
            )}
            <span>·</span>
            <span>{new Date(tx.dtRegist).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
            {tx.adminName && (
              <span className="rounded bg-amber-50 px-1 text-amber-700">관리자: {tx.adminName}</span>
            )}
            {expireBadge && (
              <span className={`rounded px-1 ${expireBadge.cls}`}>{expireBadge.label}</span>
            )}
            {isCredit && tx.dtExpire == null && tx.cdFundSource && tx.cdFundSource !== 'PG_PAYMENT' && (
              <span className="rounded bg-gray-100 px-1 text-gray-500">만료 없음</span>
            )}
          </div>
          {tx.reason && (
            <div className="text-[10px] text-gray-500 italic mt-0.5">사유: {tx.reason}</div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className={`text-sm font-bold tabular-nums ${isCredit ? 'text-emerald-600' : 'text-gray-900'}`}>
            {isCredit ? '+' : '-'}{tx.amount.toLocaleString()}
          </div>
          {(() => {
            const r = computeReversibleAmount(tx);
            if (r.credits <= 0) return null;
            const tip = r.feeApplied
              ? `환불 위약금 10% 차감: ₩${(r.refundableKrw + r.refundFeeKrw).toLocaleString()} → ₩${r.refundableKrw.toLocaleString()}`
              : '청약철회 기간 (7일 이내 + 미사용) — 전액 환불';
            return (
              <div className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700 tabular-nums" title={tip}>
                환원 {r.credits.toLocaleString()}
                {r.refundableKrw > 0 && (
                  <span className="ml-1 text-blue-500">
                    · ₩{r.refundableKrw.toLocaleString()}
                    {r.feeApplied && <span className="ml-0.5 text-amber-600">(-10%)</span>}
                  </span>
                )}
              </div>
            );
          })()}
          {reversible && (
            <button
              type="button"
              onClick={() => onReverse?.(tx)}
              className="rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700"
              title="이 트랜잭션을 환원합니다"
            >
              ↶ 환원
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  환원 모달
 * ───────────────────────────────────────────────────────────────── */
function ReverseModal({
  tx, onClose, onDone,
}: {
  tx: CompanyTxItem;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const canSave = reason.trim().length >= 5 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await creditApi.reverseTx(tx.txSeq, reason);
      onDone();
    } catch (e) {
      alert('환원 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  const isCreditOrig = tx.cdDirection === 'CREDIT';
  return (
    <Modal isOpen onClose={onClose} title={`트랜잭션 환원 — TX #${tx.txSeq}`} size="md">
      <div className="space-y-4">
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
          <strong>원본 트랜잭션</strong><br />
          <span className="text-[11px]">{CREDIT_TX_LABEL[tx.cdTxType] ?? tx.cdTxType}</span> ·
          <span className="ml-1">{isCreditOrig ? '+' : '-'}{tx.amount.toLocaleString()} 크레딧</span><br />
          <span className="text-[11px]">{new Date(tx.dtRegist).toLocaleString('ko-KR')}</span><br />
          {tx.description && <span className="text-[11px] italic">"{tx.description}"</span>}
        </div>

        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          {isCreditOrig ? (
            <>
              💡 원본이 <strong>지급</strong>이므로 환원 시 잔액이 <strong>회수</strong>됩니다.<br />
              이미 사용된 분 ({tx.amount - (tx.remainingAmt ?? 0)}크레딧) 은 회수 불가, 미사용 {tx.remainingAmt ?? 0} 만 회수됩니다.
            </>
          ) : (
            <>
              💡 원본이 <strong>차감</strong>이므로 환원 시 잔액이 <strong>복원</strong>됩니다.<br />
              복원분은 FREE 잔액으로 들어가며 만료는 없습니다.
            </>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700">환원 사유 <span className="text-red-500">*</span></label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 사장님 요청 - 알림장 생성 중 오류로 결과 미저장 / 시스템 잘못된 차감 복구"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">감사 로그에 영구 저장됩니다. 최소 5자.</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
            ↶ 환원 확정
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  전체 환원 모달 — 사업장의 환원 가능한 모든 TX 일괄 환원
 * ───────────────────────────────────────────────────────────────── */
function BulkReverseModal({
  companySeq, companyName, totalCount, totalCredits, refundableKrw,
  onClose, onDone,
}: {
  companySeq: number;
  companyName: string;
  totalCount: number;
  totalCredits: number;
  refundableKrw: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<null | {
    success: number; skipped: number; restored: number; recovered: number;
  }>(null);

  const canSave = reason.trim().length >= 5 && !saving && !result;

  const handleSave = async () => {
    if (!canSave) return;
    if (!window.confirm(`${companyName} 의 환원 가능한 ${totalCount}건을 모두 환원합니다. 진행할까요?`)) return;
    setSaving(true);
    try {
      const r = await creditApi.bulkReverseCompany(companySeq, reason);
      setResult({
        success: r.successCount,
        skipped: r.skippedCount,
        restored: r.restoredCredits,
        recovered: r.recoveredCredits,
      });
    } catch (e) {
      alert('전체 환원 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`${companyName} — 전체 환원`} size="md">
      <div className="space-y-4">
        {result ? (
          <>
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
              <div className="font-semibold mb-1">✓ 전체 환원 완료</div>
              <ul className="space-y-0.5 text-xs">
                <li>성공: <strong className="tabular-nums">{result.success}</strong>건</li>
                {result.skipped > 0 && (
                  <li className="text-amber-700">스킵: <strong className="tabular-nums">{result.skipped}</strong>건 (이미 환원/사용 완료/만료)</li>
                )}
                <li>잔액 복원 (차감 환원): <strong className="tabular-nums">{result.restored.toLocaleString()}</strong> 크레딧</li>
                <li>잔액 회수 (지급 환원): <strong className="tabular-nums">{result.recovered.toLocaleString()}</strong> 크레딧</li>
              </ul>
            </div>
            <div className="flex justify-end">
              <Button variant="primary" onClick={onDone}>닫기</Button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 space-y-1">
              <div>
                <strong>{totalCount}건</strong> 의 트랜잭션이 환원 대상입니다.
              </div>
              <div className="text-[11px]">
                총 환원 크레딧: <strong className="tabular-nums">{totalCredits.toLocaleString()}</strong>
                {refundableKrw > 0 && (
                  <> · PG 환불 가능 <strong className="tabular-nums">{refundableKrw.toLocaleString()}원</strong></>
                )}
              </div>
            </div>

            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              ⚠ <strong>되돌릴 수 없는 작업</strong>입니다.
              <br />
              · 지급 TX → 미사용 잔량 회수 (사용분은 회수 불가)<br />
              · 차감 TX → 잔액 복원 (FREE 풀로 들어감)<br />
              · 이미 환원·만료·전액 사용된 TX 는 자동 스킵<br />
              · 모든 환원 TX 는 동일 사유로 감사 로그 1행씩 기록
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700">
                환원 사유 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="예: 사업자 폐업 처리에 따른 잔여 크레딧 일괄 환원 및 PG 환불 진행"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">감사 로그에 영구 저장. 최소 5자.</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>취소</Button>
              <Button variant="danger" onClick={handleSave} disabled={!canSave} loading={saving}>
                ↶ 전체 환원 실행
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  지급/차감 모달
 * ───────────────────────────────────────────────────────────────── */
function AdjustModal({
  mode, companySeq, companyName, currentBalance, onClose, onDone,
}: {
  mode: 'grant' | 'deduct';
  companySeq: number;
  companyName: string;
  currentBalance: number;
  onClose: () => void;
  onDone: () => void;
}) {
  // 차감(deduct) 은 deprecated — 안내 후 환원 사용 권장
  if (mode === 'deduct') {
    return <DeductDeprecatedModal companySeq={companySeq} companyName={companyName} currentBalance={currentBalance}
                                    onClose={onClose} onDone={onDone} />;
  }
  return <GrantModal companySeq={companySeq} companyName={companyName} currentBalance={currentBalance}
                     onClose={onClose} onDone={onDone} />;
}

/* ─────────────────────────────────────────────────────────────────
 *  지급 모달 — 3 카테고리 (OPERATION / BUG_COMP / PURCHASE_MANUAL)
 * ───────────────────────────────────────────────────────────────── */
function GrantModal({
  companySeq, companyName, currentBalance, onClose, onDone,
}: {
  companySeq: number;
  companyName: string;
  currentBalance: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [category, setCategory] = useState<CreditGrantCategory>('OPERATION');
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // OPERATION 메타
  const [campaignName, setCampaignName] = useState('');

  // BUG_COMP 메타
  // BUG_COMP 카테고리는 OPERATION 으로 통합 — state 제거.
  // (BE 의 enum/DB 는 유지하되 관리자 UI 에서 신규 발급 불가.)

  // PURCHASE_MANUAL 메타
  const [pgTxnId, setPgTxnId] = useState('');
  const [purchasePriceKrw, setPurchasePriceKrw] = useState<number | ''>('');
  const [purchaseDt, setPurchaseDt] = useState('');
  const [packageSeq, setPackageSeq] = useState<number | ''>('');

  // 만료 (OPERATION 만)
  const [expireMode, setExpireMode] = useState<ExpireMode>('DEFAULT');
  const [expireDays, setExpireDays] = useState<number>(14);
  const [expireDate, setExpireDate] = useState('');

  // 활성 패키지 목록 (PURCHASE_MANUAL 카테고리에서 select 로 사용)
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  useEffect(() => {
    creditApi.listPackages()
      .then((list) => setPackages(list.filter((p) => p.activeYn === 'Y')))
      .catch(() => setPackages([]));
  }, []);

  const meta = GRANT_CATEGORY_META[category];
  const newBalance = currentBalance + amount;

  /** 카테고리별 필수 필드 충족 여부. */
  const fieldsValid = (() => {
    if (category === 'OPERATION') return campaignName.trim().length > 0;
    if (category === 'PURCHASE_MANUAL') {
      // PG 거래 ID·패키지 SEQ 는 선택. 결제 금액만 필수 (환불 단가 계산용)
      return typeof purchasePriceKrw === 'number' && purchasePriceKrw > 0;
    }
    return false;
  })();

  const expireValid = (() => {
    if (category !== 'OPERATION') return true;
    if (expireMode === 'DAYS')  return expireDays >= 1 && expireDays <= 3650;
    if (expireMode === 'DATE')  return !!expireDate;
    return true; // DEFAULT / NEVER
  })();

  // 사유는 필수 아님 — 카테고리/캠페인명/PG 거래 ID 등으로 추적 가능
  const canSave = amount > 0 && fieldsValid && expireValid && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload: GrantRequest = {
        amount,
        reason,
        grantCategory: category,
        ...(category === 'OPERATION' && {
          campaignName,
          expireMode,
          expireDays: expireMode === 'DAYS' ? expireDays : undefined,
          expireDate: expireMode === 'DATE' ? expireDate : undefined,
        }),
        ...(category === 'PURCHASE_MANUAL' && {
          pgTxnId,
          purchasePriceKrw: typeof purchasePriceKrw === 'number' ? purchasePriceKrw : 0,
          purchaseDt: purchaseDt || undefined,
          packageSeq: typeof packageSeq === 'number' ? packageSeq : undefined,
        }),
      };
      await creditApi.grantToCompany(companySeq, payload);
      onDone();
    } catch (e) {
      alert('지급 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  // 입력 공통 스타일
  const inputCls = "w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";
  const labelCls = "text-[11px] font-medium text-gray-700";

  return (
    <Modal isOpen onClose={onClose} title={`${companyName} — 크레딧 지급`} size="lg">
      <div className="space-y-3">
        {/* 한 행 — 잔액 / 지급액 / 예상 */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <div className="text-[10px] text-gray-500">현재 잔액</div>
            <div className="text-lg font-bold text-gray-900">{formatNum(currentBalance)}</div>
          </div>
          <div className="rounded-md border-2 border-gray-200 bg-white px-2 py-1.5">
            <div className="text-[10px] text-gray-500">지급할 크레딧 *</div>
            <input
              type="number" min={1} value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
              className="w-full bg-transparent text-lg font-bold focus:outline-none"
            />
          </div>
          <div className="rounded-md bg-emerald-50 px-3 py-2">
            <div className="text-[10px] text-gray-500">지급 후</div>
            <div className="text-lg font-bold text-emerald-700">{formatNum(newBalance)}</div>
          </div>
        </div>

        {/* 빠른 선택 — 인라인 */}
        <div className="flex flex-wrap gap-1">
          {[50, 100, 200, 500, 1000].map((v) => (
            <button key={v} type="button" onClick={() => setAmount(v)}
              className="rounded-md border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 hover:border-primary-500 hover:text-primary-600">
              +{v}
            </button>
          ))}
        </div>

        {/* 카테고리 — 가로 2분할 (BUG_COMP 는 OPERATION 으로 흡수) */}
        <div>
          <label className={labelCls}>지급 사유 분류 <span className="text-red-500">*</span></label>
          <div className="mt-1 grid grid-cols-2 gap-2">
            {VISIBLE_GRANT_CATEGORIES.map((c) => {
              const m = GRANT_CATEGORY_META[c];
              const selected = category === c;
              const selectedCls =
                c === 'PURCHASE_MANUAL'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-emerald-500 bg-emerald-50';
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={`rounded-md border-2 px-3 py-2 text-center transition-colors ${
                    selected ? selectedCls : 'border-gray-200 bg-white hover:border-gray-400'
                  }`}
                >
                  <span className="text-base">{m.emoji}</span>
                  <span className="ml-1 text-xs font-semibold">{m.label}</span>
                  <div className="text-[10px] text-gray-500">
                    {m.pool}{m.expireSupported ? ' · 만료 가능' : ' · 만료 없음'}
                    {c === 'PURCHASE_MANUAL' && <span className="text-purple-600"> · 환불 의무</span>}
                  </div>
                </button>
              );
            })}
          </div>
          {category === 'OPERATION' && (
            <p className="mt-1 text-[10px] text-gray-500">
              💡 장애/버그 보상도 여기서 처리 — <span className="font-medium">만료 설정 "없음"</span> 선택 + 사유에 장애 ID 기재
            </p>
          )}
        </div>

        {/* 카테고리별 메타 — 콤팩트 2열 */}
        {category === 'OPERATION' && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50/30 p-2.5 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>캠페인명 *</label>
                <input
                  type="text" value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="예: 5월 신규 가입 이벤트"
                  className={`mt-1 ${inputCls}`}
                />
              </div>
              <div>
                <label className={labelCls}>만료 설정 *</label>
                <div className="mt-1 grid grid-cols-4 gap-1">
                  {([
                    { v: 'DEFAULT' as const, l: '정책' },
                    { v: 'DAYS'    as const, l: 'N일' },
                    { v: 'DATE'    as const, l: '날짜' },
                    { v: 'NEVER'   as const, l: '없음' },
                  ]).map((o) => (
                    <button
                      key={o.v} type="button"
                      onClick={() => setExpireMode(o.v)}
                      className={`rounded-md border px-1 py-1.5 text-[11px] font-medium transition-colors ${
                        expireMode === o.v
                          ? 'border-emerald-500 bg-emerald-100 text-emerald-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* 만료 모드별 추가 입력 */}
            {expireMode === 'DAYS' && (
              <div className="flex items-center gap-2">
                <input
                  type="number" min={1} max={3650} value={expireDays}
                  onChange={(e) => setExpireDays(parseInt(e.target.value) || 0)}
                  className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm"
                />
                <span className="text-[11px] text-gray-600">일 후 만료</span>
                <div className="flex flex-wrap gap-1">
                  {[7, 14, 30, 60, 90].map((d) => (
                    <button key={d} type="button" onClick={() => setExpireDays(d)}
                      className="rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-600 hover:border-primary-500">
                      {d}일
                    </button>
                  ))}
                </div>
              </div>
            )}
            {expireMode === 'DATE' && (
              <input
                type="date" min={new Date().toISOString().slice(0, 10)}
                value={expireDate}
                onChange={(e) => setExpireDate(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
            )}
            {expireMode === 'DEFAULT' && (
              <p className="text-[10px] text-emerald-700">정책 KV EXPIRE_DAYS_GRANT_OPERATION 값 적용 (기본 60일)</p>
            )}
            {expireMode === 'NEVER' && (
              <p className="text-[10px] text-emerald-700">만료 없음 — VIP/영구 보너스 등 특수 케이스</p>
            )}
          </div>
        )}

        {/* BUG_COMP 카테고리는 OPERATION 으로 통합됨 — UI 에서 제거.
            기존 데이터/enum 호환을 위해 fieldsValid·payload 분기는 유지. */}

        {category === 'PURCHASE_MANUAL' && (
          <div className="rounded-md border border-purple-200 bg-purple-50/30 p-2.5 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className={labelCls}>패키지 <span className="text-gray-400">(선택 · 결제 금액·크레딧 자동 채움)</span></label>
                <select
                  value={packageSeq === '' ? '' : packageSeq}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      setPackageSeq('');
                      return;
                    }
                    const seq = parseInt(v, 10);
                    setPackageSeq(seq);
                    const pkg = packages.find((p) => p.packageSeq === seq);
                    if (pkg) {
                      // 결제 금액 + 지급 크레딧 자동 동기화
                      setPurchasePriceKrw(pkg.priceKrw);
                      setAmount(pkg.amount);
                    }
                  }}
                  className={`mt-1 ${inputCls}`}
                >
                  <option value="">— 직접 입력 (패키지 미적용) —</option>
                  {packages.map((p) => (
                    <option key={p.packageSeq} value={p.packageSeq}>
                      {p.packageName} · {p.amount.toLocaleString()} 크레딧 / ₩{p.priceKrw.toLocaleString()}
                      {p.discountRate ? ` (${Math.round(Number(p.discountRate) * 100)}% 할인)` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>실 결제 금액 (원) *</label>
                <input
                  type="number" min={0} value={purchasePriceKrw}
                  onChange={(e) => setPurchasePriceKrw(e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                  placeholder="30000"
                  className={`mt-1 ${inputCls}`}
                />
              </div>
              <div>
                <label className={labelCls}>결제 일시 <span className="text-gray-400">(선택)</span></label>
                <input
                  type="datetime-local" value={purchaseDt}
                  onChange={(e) => setPurchaseDt(e.target.value)}
                  className={`mt-1 ${inputCls}`}
                />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>PG 거래 ID <span className="text-gray-400">(선택 · 영수증 분실 시 비워두기 가능)</span></label>
                <input
                  type="text" value={pgTxnId}
                  onChange={(e) => setPgTxnId(e.target.value)}
                  placeholder="toss_xxxxxxxxxxxxx (생략 가능)"
                  className={`mt-1 ${inputCls}`}
                />
              </div>
            </div>
            <p className="text-[10px] text-purple-900">
              ⚠ PAID 잔액 적립 — 환불 의무 발생, 만료 없음 (전금법).
              <span className="ml-1 text-purple-700">필수: 결제 금액만</span>
            </p>
          </div>
        )}

        {/* 사유 — 선택사항 */}
        <div>
          <label className={labelCls}>사유 <span className="text-gray-400">(선택 · 감사 로그)</span></label>
          <input
            type="text" value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="예: 베타 테스터 보상 / 5/10 PG 결제 누락 보정 (생략 가능)"
            className={`mt-1 ${inputCls}`}
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
            {meta.emoji} 지급 확정
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  차감 모달 — Deprecated 안내 (환원 사용 권장)
 * ───────────────────────────────────────────────────────────────── */
function DeductDeprecatedModal({
  companySeq, companyName, currentBalance, onClose, onDone,
}: {
  companySeq: number;
  companyName: string;
  currentBalance: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const insufficient = currentBalance < amount;
  const canSave = amount > 0 && reason.trim().length >= 5 && confirmed && !saving && !insufficient;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await creditApi.deductFromCompany(companySeq, amount, reason);
      onDone();
    } catch (e) {
      alert('차감 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`${companyName} — 크레딧 차감 (Deprecated)`} size="md">
      <div className="space-y-4">
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-800">
          <strong>⚠ 임의 차감은 폐기 예정 기능입니다.</strong><br />
          잘못 지급된 크레딧을 회수하려면 <strong>"트랜잭션 환원"</strong> 을 사용하세요.<br />
          환원은 원본 지급 TX 를 명확히 참조하므로 감사 추적이 정확합니다.
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700">차감할 크레딧</label>
          <input type="number" min={1} value={amount}
            onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          {insufficient && <p className="mt-1 text-xs text-red-600">잔액 부족 ({currentBalance} 보유)</p>}
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700">사유 <span className="text-red-500">*</span></label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder="환원이 불가능한 사유 명시 — 최소 5자"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-700">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          환원을 사용할 수 없는 경우임을 확인했습니다.
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="danger" onClick={handleSave} disabled={!canSave} loading={saving}>
            차감 (Deprecated)
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  플랜 변경 모달
 * ───────────────────────────────────────────────────────────────── */
const PLAN_INFO: Record<CdCreditPlan, { name: string; price: string; desc: string; color: string }> = {
  PLAN_FREE:     { name: 'Free',     price: '₩0',         desc: '매월 100 무료 + 결제 환급',          color: 'border-gray-300 text-gray-700' },
  PLAN_BASIC:    { name: 'Basic',    price: '₩29,000/월', desc: '무제한 텍스트 / 사진 200건',          color: 'border-blue-500 text-blue-700 bg-blue-50' },
  PLAN_PRO:      { name: 'Pro',      price: '₩79,000/월', desc: '무제한 전부 (사진·동영상 비전 포함)', color: 'border-purple-500 text-purple-700 bg-purple-50' },
  PLAN_BUSINESS: { name: 'Business', price: '₩199,000/월',desc: 'Pro + 다중 직원 + 우선 지원',          color: 'border-emerald-500 text-emerald-700 bg-emerald-50' },
};

function PlanChangeModal({
  companySeq, companyName, currentPlan, onClose, onDone,
}: {
  companySeq: number;
  companyName: string;
  currentPlan: CdCreditPlan;
  onClose: () => void;
  onDone: () => void;
}) {
  const [plan, setPlan] = useState<CdCreditPlan>(currentPlan);
  const [feeOverride, setFeeOverride] = useState<'NORMAL' | 'WAIVE_TRIAL' | 'PROMO'>('NORMAL');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const changed = plan !== currentPlan;
  const canSave = changed && reason.trim().length >= 5 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await creditApi.changeCompanyPlan(companySeq, plan, reason, feeOverride);
      onDone();
    } catch (e) {
      alert('플랜 변경 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`${companyName} — 플랜 변경`} size="lg">
      <div className="space-y-4">
        <div className="rounded-md bg-gray-50 px-3 py-2 text-sm">
          현재 플랜: <strong>{PLAN_INFO[currentPlan].name}</strong> ({PLAN_INFO[currentPlan].price})
        </div>

        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(PLAN_INFO) as CdCreditPlan[]).map((p) => {
            const info = PLAN_INFO[p];
            const selected = plan === p;
            return (
              <button
                key={p} type="button" onClick={() => setPlan(p)}
                className={`rounded-lg border-2 p-3 text-left transition-colors ${
                  selected ? info.color : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold">{info.name}</span>
                  {currentPlan === p && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">현재</span>
                  )}
                </div>
                <div className="mt-1 text-sm font-semibold">{info.price}</div>
                <div className="mt-1 text-xs text-gray-500">{info.desc}</div>
              </button>
            );
          })}
        </div>

        {plan !== 'PLAN_FREE' && plan !== currentPlan && (
          <div>
            <label className="text-xs font-medium text-gray-700">결제 처리</label>
            <div className="mt-1 space-y-1">
              {[
                { v: 'NORMAL' as const,      l: '정상 결제',         d: '결제 모듈을 통해 사장님이 결제' },
                { v: 'WAIVE_TRIAL' as const, l: '무료 체험 (베타)',  d: '결제 없이 1개월 무료 제공' },
                { v: 'PROMO' as const,       l: '프로모션 할인',     d: '특별 가격 또는 무료 (마케팅)' },
              ].map((o) => (
                <label key={o.v} className="flex cursor-pointer items-start gap-2 rounded-md border border-gray-200 p-2 hover:border-primary-500">
                  <input type="radio" checked={feeOverride === o.v} onChange={() => setFeeOverride(o.v)} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">{o.l}</div>
                    <div className="text-xs text-gray-500">{o.d}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-gray-700">
            변경 사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="예: 베타 테스터 선정 — Pro 1개월 무료 체험 제공"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
            {changed ? `${PLAN_INFO[plan].name} 로 변경` : '동일 플랜'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  차감 면제 기간 모달
 * ───────────────────────────────────────────────────────────────── */
function ExemptionModal({
  companySeq, companyName, currentUntil, onClose, onDone,
}: {
  companySeq: number;
  companyName: string;
  currentUntil: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const defaultUntil = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  };
  const [untilDate, setUntilDate] = useState(currentUntil?.slice(0, 10) ?? defaultUntil());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'set' | 'clear'>('set');

  const canSave =
    (mode === 'clear' && currentUntil != null && reason.trim().length >= 5 && !saving) ||
    (mode === 'set' && !!untilDate && reason.trim().length >= 5 && !saving);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (mode === 'clear') {
        await creditApi.setCompanyExemption(companySeq, null, reason);
      } else {
        await creditApi.setCompanyExemption(companySeq, new Date(untilDate).toISOString(), reason);
      }
      onDone();
    } catch (e) {
      alert('면제 설정 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`${companyName} — 차감 면제`} size="md">
      <div className="space-y-4">
        <div className="rounded-md bg-blue-50 p-3 text-xs text-blue-800">
          🛡 면제 기간 동안 사장님의 모든 크레딧 차감 액션이 무효화됩니다.<br/>
          AI 알림장·이용권 발급·SMS 등 모든 액션이 0 크레딧으로 처리.
        </div>

        {currentUntil && (
          <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>현재 적용 중</strong>: ~{new Date(currentUntil).toLocaleDateString('ko-KR')}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button" onClick={() => setMode('set')}
            className={`flex-1 rounded-md border-2 px-3 py-2 text-sm font-medium ${
              mode === 'set' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600'
            }`}
          >
            기간 설정
          </button>
          {currentUntil && (
            <button
              type="button" onClick={() => setMode('clear')}
              className={`flex-1 rounded-md border-2 px-3 py-2 text-sm font-medium ${
                mode === 'clear' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600'
              }`}
            >
              면제 해제
            </button>
          )}
        </div>

        {mode === 'set' && (
          <div>
            <label className="text-xs font-medium text-gray-700">면제 종료일</label>
            <input
              type="date" value={untilDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setUntilDate(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {[3, 7, 14, 30].map((days) => (
                <button
                  key={days} type="button"
                  onClick={() => {
                    const d = new Date(); d.setDate(d.getDate() + days);
                    setUntilDate(d.toISOString().slice(0, 10));
                  }}
                  className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-primary-500"
                >
                  +{days}일
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-gray-700">
            사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder={mode === 'set'
              ? '예: 시스템 장애 보상 (5/13 02:00~04:00 서비스 중단)'
              : '예: 면제 기간 조기 종료 사유'}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button variant={mode === 'clear' ? 'danger' : 'primary'} onClick={handleSave} disabled={!canSave} loading={saving}>
            {mode === 'clear' ? '면제 해제' : '면제 적용'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
